/**
 * When a procedure is allowed to start: one of the service's three named
 * windows, or a custom set of hours per day.
 *
 * The wire shape is a discriminated union — `{type}` for a named window, or
 * `{type: 'custom', ranges}` where each range lists ISO weekdays (Monday is 1)
 * with a `from` and `to` as `"HH:MM"` in the site's local time. Each enabled day
 * gets its own range, which is the shape that survives a round trip without
 * having to guess how days were grouped.
 *
 * The day rows follow `LocationFormHoursComponent` — Open/Closed, add, remove,
 * copy-to-day — and reuse its `TimeRangePickerWith24Hours`. That component is
 * not used directly: it is antd v3 (`getFieldDecorator`), writes a form field
 * per day rather than one value, and is bound to a location's `operatingHours`.
 */

import { Button, Checkbox, Col, Popover, Row, Select } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import moment from 'moment';
import React, { useState } from 'react';
import { TimeRangePickerWith24Hours } from '../operating_hours_card_display/TimePicker24Hour';
import {
	AOPActiveWindow,
	AOPCustomWindow,
	AOPNamedWindowType,
	AOPRange,
} from '../../types/AOPTypes';

/** ISO weekday order, which is also the order the design lists them in. */
const DAYS: { day: number; label: string }[] = [
	{ day: 1, label: 'Monday' },
	{ day: 2, label: 'Tuesday' },
	{ day: 3, label: 'Wednesday' },
	{ day: 4, label: 'Thursday' },
	{ day: 5, label: 'Friday' },
	{ day: 6, label: 'Saturday' },
	{ day: 7, label: 'Sunday' },
];

const WINDOW_OPTIONS: { value: string; label: string }[] = [
	{ value: 'operating_hours', label: 'Business hours' },
	{ value: 'after_hours', label: 'After hours' },
	{ value: 'all_hours', label: 'All day (24x7)' },
	{ value: 'custom', label: 'Custom window' },
];

const CLOCK_FORMAT = 'HH:mm';
const DEFAULT_FROM = '08:00';
const DEFAULT_TO = '18:00';
/** Monday to Friday, which is what the design shows a custom window opening on. */
const DEFAULT_CUSTOM_DAYS = [1, 2, 3, 4, 5];

/**
 * `TimeRangePickerWith24Hours` marks a full day as 23:59, because that is the
 * last minute a time picker can hold. The service spells the same thing `24:00`,
 * and its 15-minute steps mean 23:59 is unreachable any other way — so the two
 * are translated rather than losing the final minute of a full-day window.
 */
const END_OF_DAY_PICKER = '23:59';
const END_OF_DAY_WIRE = '24:00';

const toMoment = (clock: string) =>
	moment(clock === END_OF_DAY_WIRE ? END_OF_DAY_PICKER : clock, CLOCK_FORMAT);

const fromMoment = (value: any, fallback: string): string => {
	if (!value || !moment.isMoment(value)) {
		return fallback;
	}
	const clock = value.format(CLOCK_FORMAT);
	return clock === END_OF_DAY_PICKER ? END_OF_DAY_WIRE : clock;
};

interface DayRow {
	enabled: boolean;
	from: string;
	to: string;
}

type DayRows = { [day: number]: DayRow };

const emptyRows = (): DayRows => {
	const rows: DayRows = {};
	DAYS.forEach((entry) => {
		rows[entry.day] = {
			enabled: DEFAULT_CUSTOM_DAYS.indexOf(entry.day) >= 0,
			from: DEFAULT_FROM,
			to: DEFAULT_TO,
		};
	});
	return rows;
};

/** Ranges may list several days, so a stored window expands back to one row each. */
export const rowsFromWindow = (window: AOPActiveWindow): DayRows => {
	if (window.type !== 'custom') {
		return emptyRows();
	}

	const rows: DayRows = {};
	DAYS.forEach((entry) => {
		rows[entry.day] = { enabled: false, from: DEFAULT_FROM, to: DEFAULT_TO };
	});

	(window as AOPCustomWindow).ranges.forEach((range) => {
		range.days.forEach((day) => {
			rows[day] = { enabled: true, from: range.from, to: range.to };
		});
	});

	return rows;
};

const windowFromRows = (rows: DayRows): AOPCustomWindow => {
	const ranges: AOPRange[] = [];

	DAYS.forEach((entry) => {
		const row = rows[entry.day];
		if (row && row.enabled) {
			ranges.push({ days: [entry.day], from: row.from, to: row.to });
		}
	});

	return { type: 'custom', ranges };
};

/**
 * A custom window needs at least one day, and a range may not start and end at
 * the same time — the service rejects both.
 */
export const validateActiveWindow = (value: AOPActiveWindow | null): string | null => {
	if (!value) {
		return 'Pick when this procedure can start.';
	}

	if (value.type !== 'custom') {
		return null;
	}

	const ranges = (value as AOPCustomWindow).ranges;

	if (ranges.length === 0) {
		return 'Open at least one day.';
	}

	if (ranges.some((range) => range.from === range.to)) {
		return 'A day cannot start and end at the same time.';
	}

	return null;
};

interface CopyToDayProps {
	today: number;
	onCopy: (days: number[]) => void;
}

const CopyToDay: React.FC<CopyToDayProps> = ({ today, onCopy }) => {
	const [selected, setSelected] = useState<number[]>([]);
	const label = DAYS.filter((entry) => entry.day === today)[0].label;

	return (
		<div
			onMouseDown={(event) => event.stopPropagation()}
			onClick={(event) => event.stopPropagation()}
		>
			<div style={{ marginBottom: 8, fontWeight: 500 }}>Copy {label}’s hours to:</div>
			<Checkbox.Group
				value={selected}
				onChange={(values) => setSelected(values as number[])}
				style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}
				options={DAYS.filter((entry) => entry.day !== today).map((entry) => ({
					label: entry.label,
					value: entry.day,
				}))}
			/>
			<Button
				type="primary"
				size="small"
				style={{ marginTop: 8 }}
				disabled={selected.length === 0}
				onClick={() => {
					onCopy(selected);
					setSelected([]);
				}}
			>
				Copy
			</Button>
		</div>
	);
};

interface AOPActiveWindowFieldProps {
	// Optional because antd's Form.Item injects them; the field is never used
	// uncontrolled.
	value?: AOPActiveWindow | null;
	onChange?: (value: AOPActiveWindow) => void;
}

export const AOPActiveWindowField: React.FC<AOPActiveWindowFieldProps> = ({ value, onChange }) => {
	const kind = value ? value.type : undefined;
	const rows = value && value.type === 'custom' ? rowsFromWindow(value) : emptyRows();

	const emit = (next: AOPActiveWindow) => {
		if (onChange) {
			onChange(next);
		}
	};

	const writeRows = (next: DayRows) => emit(windowFromRows(next));

	const setRow = (day: number, patch: Partial<DayRow>) =>
		writeRows({ ...rows, [day]: { ...rows[day], ...patch } });

	const copyTo = (source: number, targets: number[]) => {
		const next = { ...rows };
		targets.forEach((day) => {
			next[day] = { enabled: true, from: rows[source].from, to: rows[source].to };
		});
		writeRows(next);
	};

	return (
		<div className="flex flex-col gap-2">
			<Select
				value={kind}
				placeholder="Select active window"
				onChange={(nextKind) =>
					emit(
						nextKind === 'custom'
							? windowFromRows(emptyRows())
							: { type: nextKind as AOPNamedWindowType }
					)
				}
				options={WINDOW_OPTIONS}
			/>

			{kind === 'custom' ? (
				<div className="aop-window-grid flex flex-col gap-1.5 rounded border border-solid border-grey-4 bg-grey-2 p-2.5">
					{DAYS.map((entry) => {
						const row = rows[entry.day];

						return (
							<Row key={entry.day} style={{ alignItems: 'center', minHeight: 40, gap: 8 }}>
								<Col flex="none">
									<div
										style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}
									>
										<span style={{ minWidth: 75 }}>{entry.label}</span>
										<span
											className={
												row.enabled ? 'text-lifecycleChange-negative' : 'text-character-secondary'
											}
											style={{ minWidth: 45 }}
										>
											{row.enabled ? 'Open' : 'Closed'}
										</span>
									</div>
								</Col>
								<Col flex="auto">
									<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
										{row.enabled ? (
											<>
												<TimeRangePickerWith24Hours
													name={`aop-window-${entry.day}`}
													value={{ from: toMoment(row.from), to: toMoment(row.to) }}
													onChange={(next: any) =>
														setRow(entry.day, {
															from: fromMoment(next.from, row.from),
															to: fromMoment(next.to, row.to),
														})
													}
												/>
												<div style={{ display: 'flex', gap: 2 }}>
													<Button
														className="rowButton"
														type="text"
														aria-label={`Close ${entry.label}`}
														onClick={() => setRow(entry.day, { enabled: false })}
													>
														<i className="icons8-font icons8-trash-can" />
													</Button>
													<Popover
														placement="bottomRight"
														content={
															<CopyToDay
																today={entry.day}
																onCopy={(targets) => copyTo(entry.day, targets)}
															/>
														}
													>
														<Button
															className="rowButton"
															type="text"
															aria-label={`Copy ${entry.label}`}
														>
															<CopyOutlined className="text-character-secondary" />
														</Button>
													</Popover>
												</div>
											</>
										) : (
											<Button
												className="rowButton"
												type="text"
												aria-label={`Open ${entry.label}`}
												onClick={() => setRow(entry.day, { enabled: true })}
											>
												<i className="icons8-font icons8-plus" />
											</Button>
										)}
									</div>
								</Col>
							</Row>
						);
					})}
					<p className="footnoteregular m-0 text-character-secondary">
						Radar only starts this procedure inside these hours. Times are local to the site.
					</p>
				</div>
			) : null}
		</div>
	);
};
